import type { CompletionSource } from '@codemirror/autocomplete';
import {
  CompletionContext,
  autocompletion,
  startCompletion,
} from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import type { EditorState, Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

import type { OverlayTriggerState } from '../../../overlays/hooks/useOverlay.js';
import {
  ANNOTATION_DOC_CHANGED_CONTROLLED,
  ANNOTATION_SUGGESTION_APPLY,
  ANNOTATION_SUGGESTION_PIN,
  pastingMode,
} from '../editor-state-configs/editor-config.js';
import type { FilterFieldProps } from '../FilterField.types.js';
import type {
  FilterFieldSuggestion,
  FilterFieldSuggestionProps,
} from './Suggestion.types.js';
import type { useCachedTreeConversion } from '../hooks/useCachedTreeConversion.js';
import type { NumberFormatParser } from '../parser/getNumberParser.js';
import type {
  FilterFieldGroupedSuggestions,
  FilterFieldSuggestionsCallback,
} from '../types/suggestions.js';
import type {
  FilterFieldLezerNodeType,
  FilterFieldNode,
  FilterFieldStatementNode,
  FilterFieldValueNode,
} from '../types/tree-nodes.js';
import type { FilterFieldCustomTypes } from '../types/validation.js';
import { escapeSuggestion } from '../utils/escape-suggestion.js';
import { getCurrentToken } from '../utils/suggestions-utils/get-current-token.js';
import { getSuggestionCategory } from '../utils/suggestions-utils/get-suggestion-category.js';
import { getSuggestions } from '../utils/suggestions-utils/get-suggestions.js';
import {
  getFallbackKeySuggestions,
  getFilterStatementSuggestions,
  getSearchSuggestions,
} from '../utils/suggestions-utils/key-suggestions.js';
import {
  getRecentAndPinnedSuggestions,
  type FilterFieldSuggestionRecentPinnedSuggestion,
} from '../utils/suggestions-utils/recent-and-pinned-suggestions.js';
import { getFilterStatement } from '../utils/suggestions-utils/suggestions-callback.js';
import { isCursorInsideGroup } from '../utils/tree-navigation.js';
import type { InternalValidatorMap } from '../utils/validation-utils/normalize-validator-map.js';

/** @internal */
export type FilterFieldSuggestionsContext = {
  token: FilterFieldValueNode;
  currentStatement: FilterFieldStatementNode | undefined;
};

/** @internal */
export type InternalFilterFieldSuggestionsCallback = (args: {
  suggestionsContext: FilterFieldSuggestionsContext;
  autoSuggestionValues: FilterFieldGroupedSuggestions;
  recentSuggestionValues: FilterFieldSuggestionRecentPinnedSuggestion[];
  pinnedSuggestionValues: FilterFieldSuggestionRecentPinnedSuggestion[];
  logicalOperatorSuggestionValues: FilterFieldSuggestion[];
  pastedContentSuggestionValues: FilterFieldSuggestion[];
  searchSuggestionValues: FilterFieldSuggestion[];
  fallbackKeySuggestionValues: FilterFieldSuggestion[];
  filterStatementSuggestionValues: FilterFieldSuggestion[];
}) => void;

type SuggestionsExtensionProps = {
  overlayState: OverlayTriggerState;
  numberParser: NumberFormatParser;
  internalValidatorMap?: InternalValidatorMap;
  /**
   * Internal callback that is triggered whenever suggestions may need to be updated.
   * Used to update flags to determine which suggestions to show in the suggestions
   * overlay and update the suggestions context.
   */
  _onSuggest: InternalFilterFieldSuggestionsCallback;
  /** Consumer callback triggered whenever suggestions may need to be updated. */
  onSuggest?: FilterFieldSuggestionsCallback;
  /** Function to get the filter field syntax tree from the lezer syntax tree. */
  getSyntaxTree: ReturnType<typeof useCachedTreeConversion>['getSyntaxTree'];
  /** Config to enable new nodes being returned in the FilterFieldTree */
  parserConfig?: FilterFieldProps['parserConfig'];
  customTypes?: FilterFieldCustomTypes;
};

/**
 * @internal
 */
export const suggestionsExtension = ({
  overlayState,
  numberParser,
  internalValidatorMap,
  onSuggest,
  _onSuggest,
  getSyntaxTree,
  customTypes,
  parserConfig,
}: SuggestionsExtensionProps): Extension => {
  const filterFieldSuggestion: CompletionSource = (
    context: CompletionContext,
  ) => {
    if (context.view?.composing) {
      return null;
    }

    const tree = syntaxTree(context.state);
    const { tree: convertedTree } = getSyntaxTree({
      tree,
      numberParser,
      doc: context.state.doc.toString(),
      customTypes,
      parserConfig,
    });

    const pastingStateField = context.view?.state.field(pastingMode, false);

    // context.pos returns the first position it finds if a selection is present, which may be the anchor.
    // We always want the cursor position, represented by the selection head.
    const { head: cursorPosition } = context.state.selection.main;
    let currentStatement = getFilterStatement(
      convertedTree,
      cursorPosition,
      context.state.doc.toString(),
    );

    const token = getCurrentToken({
      tree,
      convertedTree,
      cursorPosition,
      doc: context.state.doc.toString(),
    });
    let currentToken = token?.currentToken;
    const currentTokenType = token?.currentTokenType;

    const treeCursor = tree.cursorAt(cursorPosition, -1);
    // Move to first child ending before cursor position.
    treeCursor.childBefore(cursorPosition);
    const prevNode =
      treeCursor.node.type.is('FilterValue') ||
      treeCursor.node.parent?.type.is('FilterValue')
        ? 'FilterValue'
        : (treeCursor.node.name as FilterFieldLezerNodeType);

    const doc = context.state.doc;

    const {
      showInclusionOperators,
      showLogicalOperators,
      suggestionType,
      overlappingSuggestions,
    } = getSuggestionCategory({
      currentStatement,
      currentToken,
      currentTokenType,
      prevNode,
      tree,
      doc: context.state.doc.toString(),
      cursorPosition,
    });

    const suggestionTypes = Array.from(
      new Set(
        [suggestionType].concat(
          // Using concat with the ternary so that typescript can correctly infer the array's type.
          overlappingSuggestions ? overlappingSuggestions.type : [],
        ),
      ),
    );

    let suggestionTypeForToken = suggestionTypes.map((type) => ({
      type,
      token:
        type === overlappingSuggestions?.type
          ? overlappingSuggestions.subToken
          : currentToken,
    }));

    if (
      pastingStateField &&
      pastingStateField.pastingInProgress &&
      pastingStateField.range
    ) {
      suggestionTypes.push('pastedContent');

      currentStatement = undefined;
      const textValue = doc.sliceString(
        pastingStateField.range.from,
        pastingStateField.range.to,
      );

      const escapedTextValue = escapeSuggestion(textValue);

      const pastedContentToken: FilterFieldNode = {
        range: {
          from: pastingStateField.range.from,
          to: pastingStateField.range.to,
        },
        value: textValue,
        textValue: escapedTextValue,
        type: 'String',
      };

      const { token, type } = getCurrentTokenForPastedContent(
        currentToken,
        pastingStateField.range,
        context.state,
        pastedContentToken,
      );

      if (type) {
        suggestionTypeForToken = [
          {
            type: type,
            token: token as FilterFieldValueNode,
          },
        ];

        currentToken = token as FilterFieldValueNode;
      }

      suggestionTypeForToken.push({
        type: 'pastedContent',
        token: pastedContentToken,
      });
    }

    const {
      logicalOperatorSuggestions,
      groupedSuggestions,
      pastedContentSuggestions,
    } = getSuggestions({
      suggestionTypes,
      overlappingSuggestions,
      currentStatement,
      currentToken,
      currentTokenType,
      prevNode,
      internalValidatorMap,
      customTypes,
      showLogicalOperators,
      showInclusionOperators,
      doc: context.state.doc.toString(),
      suggestionTypeForToken,
    });

    const { recentSuggestionValues, pinnedSuggestionValues } =
      getRecentAndPinnedSuggestions(context);

    const searchSuggestionValues = getSearchSuggestions(
      context,
      currentStatement,
      currentToken,
      suggestionTypes,
      isCursorInsideGroup(context.state, cursorPosition),
    );

    let filterStatementSuggestions: FilterFieldSuggestionProps[] = [];

    if (currentToken.type !== 'List') {
      filterStatementSuggestions = getFilterStatementSuggestions(
        currentToken,
        suggestionTypes,
        internalValidatorMap?.filterStatementKeysMap,
      );
    }

    const fallbackKeySuggestions = getFallbackKeySuggestions(
      currentToken,
      suggestionTypes,
      internalValidatorMap?.fallbackKeys,
      filterStatementSuggestions,
    );

    _onSuggest({
      suggestionsContext: {
        token: currentToken,
        currentStatement,
      },
      autoSuggestionValues: groupedSuggestions,
      recentSuggestionValues,
      pinnedSuggestionValues,
      logicalOperatorSuggestionValues: logicalOperatorSuggestions,
      pastedContentSuggestionValues: pastedContentSuggestions,
      searchSuggestionValues,
      fallbackKeySuggestionValues: fallbackKeySuggestions,
      filterStatementSuggestionValues: filterStatementSuggestions,
    });

    onSuggest?.({
      suggestionTypes,
      currentTokens: suggestionTypeForToken,
      value: context.state.doc.toString(),
      groupedSuggestions,
      syntaxTree: convertedTree,
      cursorPosition,
      currentStatement,
    });

    // Open the suggestions overlay only after the callbacks have been called.
    // Else, previous suggestions may still be rendered in the overlay and flicker
    // when updated in the most recent call to 'onSuggest'.
    overlayState.open();

    // Return empty suggestions array to avoid opening codemirror's suggestions overlay.
    return null;
  };

  return [
    autocompletion({ override: [filterFieldSuggestion] }),
    EditorView.updateListener.of((update) => {
      // Don't do anything while a character composition is ongoing. Users would no longer
      // be able to select in the autocompletion dropdown for e.g. completing weird japanese shit.
      if (update.view.composing) {
        return;
      }

      // Trigger autocompletion to call the `onAutocomplete` callback and open the
      // suggestions overlay.
      if (
        (update.focusChanged && update.view.hasFocus) ||
        update.transactions.some(
          (transaction) =>
            transaction.isUserEvent('input') ||
            transaction.isUserEvent(ANNOTATION_SUGGESTION_APPLY) ||
            transaction.isUserEvent(ANNOTATION_SUGGESTION_PIN) ||
            transaction.isUserEvent('delete') ||
            (transaction.selection !== undefined &&
              (transaction.isUserEvent(ANNOTATION_DOC_CHANGED_CONTROLLED) ||
                update.view.hasFocus)),
        )
      ) {
        startCompletion(update.view);
        return;
      }

      if (!update.view.hasFocus) {
        overlayState.close();
        return;
      }
    }),
  ];
};

function getCurrentTokenForPastedContent(
  currentToken: FilterFieldValueNode,
  pastingRange: {
    from: number;
    to: number;
  },
  state: EditorState,
  pastedContentToken: FilterFieldNode,
): { token: FilterFieldNode; type: 'key' | 'value' | undefined } {
  // If the pasting range is bigger than the current token, we have to handle it, otherwise we can use the current token as is.
  if (
    pastingRange.from >= currentToken.range.from &&
    pastingRange.to <= currentToken.range.to
  ) {
    return { token: currentToken, type: undefined };
  }

  return getTokenBeforePastedContent(
    pastingRange.from,
    state,
    pastedContentToken,
  );
}

/**
 * Check for a FreeText or Identifier node directly after the given position
 * and return its `to` offset, or `null` if none exists.
 */
function getTrailingNodeEnd(
  tree: ReturnType<typeof syntaxTree>,
  position: number,
): number | null {
  const trailingCursor = tree.cursorAt(position, 1);
  if (
    (trailingCursor.node.name === 'FreeText' ||
      trailingCursor.node.name === 'Identifier') &&
    trailingCursor.node.from <= position
  ) {
    return trailingCursor.node.to;
  }
  return null;
}

/** @internal */
export function getTokenBeforePastedContent(
  pastedRangeFrom: number,
  editorState: EditorState,
  pastedContentToken: FilterFieldNode,
): { token: FilterFieldNode; type: 'key' | 'value' | undefined } {
  const tree = syntaxTree(editorState);
  const cursor = tree.cursorAt(pastedRangeFrom, -1);

  // If the node is the Filter, the FilterField was empty before pasting or the
  // start of a new statement.
  if (cursor.node.name === 'Filter') {
    return { token: pastedContentToken, type: 'key' };
  }

  // E.g. a =pastedContent or a = pastedContent
  if (
    cursor.node.name === 'FilterStatement' ||
    cursor.node.name === 'ComparisonOperator'
  ) {
    const rangeTo = getTrailingNodeEnd(tree, pastedContentToken.range.to);
    if (rangeTo !== null) {
      const textValue = editorState.doc.sliceString(
        pastedContentToken.range.from,
        rangeTo,
      );
      return {
        token: {
          range: { from: pastedContentToken.range.from, to: rangeTo },
          value: textValue,
          textValue: escapeSuggestion(textValue),
          type: 'String',
        },
        type: 'value',
      };
    }

    return { token: pastedContentToken, type: 'value' };
  }

  // Include any leading freetext or identifier nodes before the pasted content.
  // Also include any trailing freetext or identifier nodes after the pasted content
  // that were part of the original token before pasting.
  // e.g. freetext |pastedContent or identifier|pastedContent|trailingIdentifier
  if (cursor.node.name === 'FreeText' || cursor.node.name === 'Identifier') {
    const rangeFrom = cursor.node.from;
    const rangeTo =
      getTrailingNodeEnd(tree, pastedContentToken.range.to) ??
      pastedContentToken.range.to;

    const textValue = editorState.doc.sliceString(rangeFrom, rangeTo);

    const combinedToken: FilterFieldNode = {
      range: { from: rangeFrom, to: rangeTo },
      value: textValue,
      textValue: escapeSuggestion(textValue),
      type: 'String',
    };

    return {
      token: combinedToken,
      type: cursor.node.name === 'FreeText' ? 'key' : 'value',
    };
  }

  return { token: pastedContentToken, type: undefined };
}
